import type {
  AccountManualType,
  AccountTableView,
} from "@vault/web-vault/app/models/types/account.types";
import { Origin } from "@vault/web-vault/app/models/types/general-types";

export type TableSource = AccountTableView;

export type AccountManualTypeFilter = { [key in AccountManualType]: boolean };
export type AccountOriginFilter = Partial<{ [key in Origin]: boolean }>;

export type TableFilter = {
  manual: AccountManualTypeFilter;
  synced: AccountOriginFilter;
};

/**
 * Default account-table filter: every togglable option selected. Only `fiskil`
 * currently has a checkbox (`basiq`/`plaid`/`plugin` aren't in `syncedOrigins`);
 * the other `synced` keys here are enumerated redundantly. Under `filterAccountRows`'
 * `!== false` semantics an enumerated `true` and an absent key behave
 * identically, so checkbox-less origins (e.g. `system` / GLOSS Credits) default
 * to visible whether or not they're listed. Exported so the component and its
 * specs share one source of truth instead of hand-copying the literal.
 */
export const DEFAULT_ACCOUNT_TABLE_FILTER: TableFilter = {
  manual: { bank: true, credit: true, other: true, stock: true },
  synced: { basiq: true, plaid: true, fiskil: true, plugin: true },
};

export function compareByInstitutionThenName(a: AccountTableView, b: AccountTableView): number {
  const instCompare = (a.institution || "").trim().localeCompare((b.institution || "").trim());
  if (instCompare !== 0) {
    return instCompare;
  }
  return (a.name || "").trim().localeCompare((b.name || "").trim());
}

/**
 * Pure filtering logic for the account details table. Extracted from the
 * subscription so it can be unit-tested without Angular DI (see the class
 * comment in account-details-table.subscription.ts).
 */
export function filterAccountRows(selectedFilter: TableFilter, data: TableSource[]) {
  // `data` is undefined until the enriched accounts collection resolves
  // (getAccountTableViews does `accountViews?.map(...)`). Pass it straight
  // through as the loading sentinel — matching the early returns below — so a
  // partial filter selected during the loading window doesn't call
  // `.filter()` on undefined and crash change detection.
  if (!data) {
    return data;
  }

  if (!selectedFilter) {
    return data;
  }

  const manualFilters = selectedFilter.manual;
  const syncedFilters = selectedFilter.synced;

  // Check if all filters are selected (default state - show everything)
  const allManualSelected = manualFilters && Object.values(manualFilters).every((value) => value);
  const allSyncedSelected = syncedFilters && Object.values(syncedFilters).every((value) => value);

  // If all filters are selected, show all data
  if (allManualSelected && allSyncedSelected) {
    return data;
  }

  return data.filter((row) => {
    const account = row.original;
    const isManualAccount = account.origin === "manual";

    // For manual accounts, check if their type is selected
    if (isManualAccount) {
      return manualFilters?.[row.type as AccountManualType] ?? false;
    }

    // For synced / system-generated origins, hide a row only when the user has
    // explicitly turned that origin OFF. Origins with no checkbox (e.g. `plugin`,
    // and `system` / GLOSS Credits) are absent from the filter map, so they
    // default to visible — a new Origin enum value can't silently disappear
    // under a partial filter the way `system` did.
    return syncedFilters?.[account.origin as Origin] !== false;
  });
}
